import React, { useMemo, useState } from "react";

const CP_WATER = 4180; // J/kg-K

function clamp(n, lo, hi) {
  return Math.max(lo, Math.min(hi, n));
}

// Rough water density vs temp (0–100C)
function rhoWater(tempC) {
  const t = clamp(tempC, 0, 100);
  return 999.84 + (958.4 - 999.84) * (t / 100);
}

// Log-mean temperature difference with a single cold-side reference temp.
// In MIXED mode that cold-side is the tank bulk temp.
// In STRATIFIED mode that cold-side is the bottom-layer (local around coil) temp.
function lmtd(tHotIn, tHotOut, tCold) {
  const dT1 = tHotIn - tCold;
  const dT2 = tHotOut - tCold;

  const a = Math.max(dT1, 1e-6);
  const b = Math.max(dT2, 1e-6);

  if (Math.abs(a - b) < 1e-6) return a;
  return (a - b) / Math.log(a / b);
}

// Solve Q from: Q = UA * LMTD(Tin, Tout, Tcold)
// with Tout = Tin - Q/(m_dot cp)
function solveQ(UA, Tin, mdot, Tcold) {
  if (UA <= 0 || mdot <= 0) return 0;

  let Q = UA * Math.max(0, Tin - Tcold);

  for (let i = 0; i < 40; i++) {
    const Tout = Tin - Q / (mdot * CP_WATER);

    // physical clamp: Tout cannot go below cold-side reference
    const ToutClamped = Math.max(Tout, Tcold + 0.01);

    const dTlm = lmtd(Tin, ToutClamped, Tcold);
    const Qnew = UA * dTlm;

    if (!Number.isFinite(Qnew)) break;
    if (Math.abs(Qnew - Q) < 0.25) {
      Q = Qnew;
      break;
    }
    Q = Qnew;
  }

  // absolute max energy possible if hot stream cooled to cold-side reference temp
  const Qmax = mdot * CP_WATER * Math.max(0, Tin - Tcold);
  return clamp(Q, 0, Qmax);
}

function fmt(n, dp = 3) {
  return Number.isFinite(n) ? n.toFixed(dp) : "—";
}

function Badge({ children, tone = "purple" }) {
  const cls =
    tone === "purple"
      ? "bg-purple-500/10 border-purple-500/30 text-purple-200"
      : tone === "zinc"
      ? "bg-zinc-800/60 border-white/10 text-zinc-200"
      : "bg-emerald-500/10 border-emerald-500/30 text-emerald-200";
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs ${cls}`}>
      {children}
    </span>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className="rounded-xl bg-zinc-800/60 border border-white/10 p-4">
      <div className="text-xs uppercase tracking-wide text-zinc-400">{label}</div>
      <div className="mt-1 text-lg font-semibold text-zinc-100">{value}</div>
      {sub ? <div className="mt-1 text-xs text-zinc-400">{sub}</div> : null}
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1">
      <div className="text-sm text-zinc-400">{k}</div>
      <div className="text-sm font-medium text-zinc-100 text-right">{v}</div>
    </div>
  );
}

export default function CoilUAEstimator() {
  // Geometry
  const [lengthM, setLengthM] = useState(6);
  const [odMm, setOdMm] = useState(25.7);

  // Boiler / primary
  const [tinC, setTinC] = useState(80);
  const [flowLpm, setFlowLpm] = useState(25);

  // Mode: Mixed vs Stratified
  const [mode, setMode] = useState("stratified"); // "mixed" | "stratified"

  // Stratified context temps (top is just context; bottom drives the heat transfer calc)
  const [topC, setTopC] = useState(60);
  const [bottomC, setBottomC] = useState(15);

  // U presets (overall U based on OUTSIDE area)
  const presets = { conservative: 350, typical: 500, optimistic: 800 };
  const [preset, setPreset] = useState("typical");
  const [U, setU] = useState(presets[preset]);

  function applyPreset(p) {
    setPreset(p);
    setU(presets[p]);
  }

  const base = useMemo(() => {
    const L = Math.max(0, lengthM);
    const Dout = Math.max(0, odMm) / 1000;

    const Aout = Math.PI * Dout * L; // m2

    const rho = rhoWater(tinC);
    const flowM3s = Math.max(0, flowLpm) / 60 / 1000;
    const mdot = rho * flowM3s;

    const UA = Math.max(0, U) * Aout; // W/K

    return { Aout, rho, flowM3s, mdot, UA };
  }, [lengthM, odMm, tinC, flowLpm, U]);

  const tableTemps = useMemo(() => {
    // In MIXED mode, these are bulk tank temps.
    // In STRATIFIED mode, these are the LOCAL bottom-layer temps around the coil.
    return [10, 40, 60, 65];
  }, []);

  const points = useMemo(() => {
    return tableTemps.map((Tcold) => {
      const Q_W = solveQ(base.UA, tinC, base.mdot, Tcold);
      const Q_kW = Q_W / 1000;

      const Qmax_W = base.mdot * CP_WATER * Math.max(0, tinC - Tcold);
      const Qmax_kW = Qmax_W / 1000;

      const Tout = base.mdot > 0 ? tinC - Q_W / (base.mdot * CP_WATER) : tinC;
      const dT = tinC - Tout;

      const dTlm = lmtd(tinC, Math.max(Tout, Tcold + 0.01), Tcold);

      return { Tcold, Q_kW, Tout, dT, dTlm, Qmax_kW };
    });
  }, [base.UA, base.mdot, tinC, tableTemps]);

  const live = useMemo(() => {
    const Tcold =
      mode === "mixed"
        ? clamp(bottomC, 0, 90) // treat "bottomC" input as bulk in mixed mode to keep UI simple
        : clamp(bottomC, 0, 90); // in stratified mode, bottomC is the local temp around the coil

    const Q_W = solveQ(base.UA, tinC, base.mdot, Tcold);
    const Q_kW = Q_W / 1000;

    const Tout = base.mdot > 0 ? tinC - Q_W / (base.mdot * CP_WATER) : tinC;
    const dT = tinC - Tout;

    const dTlm = lmtd(tinC, Math.max(Tout, Tcold + 0.01), Tcold);

    return { Tcold, Q_kW, Tout, dT, dTlm };
  }, [mode, bottomC, base.UA, base.mdot, tinC]);

  return (
    <div className="max-w-4xl mx-auto p-6 rounded-2xl bg-zinc-900 text-zinc-100 shadow-xl border border-white/10">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-purple-400">Boiler Coil Estimator</h2>
          <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
            <span className="text-zinc-200 font-medium">Stratified mode</span> treats the cold-side temp as the{" "}
            <span className="text-zinc-200 font-medium">local bottom-layer temperature around the coil</span> (no mixing).
            That’s the correct knob if your cylinder stays stratified.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Badge tone="purple">{tinC}°C primary</Badge>
          <Badge tone="emerald">{flowLpm} L/min</Badge>
          <Badge tone="zinc">U={fmt(U, 0)} W/m²·K</Badge>
        </div>
      </div>

      {/* Controls */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-2xl border border-white/10 bg-zinc-800/40 p-4">
          <div className="text-xs uppercase tracking-wide text-zinc-400 mb-3">Geometry</div>

          <label className="block text-sm text-zinc-300">
            Coil length (m)
            <input
              className="mt-1 w-full rounded-md bg-zinc-900 border border-white/10 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
              type="number"
              step="0.1"
              value={lengthM}
              onChange={(e) => setLengthM(Number(e.target.value))}
            />
          </label>

          <label className="block text-sm text-zinc-300 mt-3">
            Tube OD (mm)
            <input
              className="mt-1 w-full rounded-md bg-zinc-900 border border-white/10 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
              type="number"
              step="0.1"
              value={odMm}
              onChange={(e) => setOdMm(Number(e.target.value))}
            />
          </label>
        </div>

        <div className="rounded-2xl border border-white/10 bg-zinc-800/40 p-4">
          <div className="text-xs uppercase tracking-wide text-zinc-400 mb-3">Primary (boiler)</div>

          <label className="block text-sm text-zinc-300">
            Inlet temp (°C)
            <input
              className="mt-1 w-full rounded-md bg-zinc-900 border border-white/10 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
              type="number"
              step="1"
              value={tinC}
              onChange={(e) => setTinC(Number(e.target.value))}
            />
          </label>

          <label className="block text-sm text-zinc-300 mt-3">
            Flow (L/min)
            <input
              className="mt-1 w-full rounded-md bg-zinc-900 border border-white/10 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
              type="number"
              step="0.1"
              value={flowLpm}
              onChange={(e) => setFlowLpm(Number(e.target.value))}
            />
          </label>

          <div className="mt-3 text-xs text-zinc-400">
            ρ from inlet temp, Cp fixed at 4180 J/kg·K.
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-zinc-800/40 p-4">
          <div className="text-xs uppercase tracking-wide text-zinc-400 mb-3">Tank behaviour</div>

          <div className="flex gap-2">
            <button
              onClick={() => setMode("mixed")}
              className={`flex-1 px-3 py-2 rounded-lg text-sm border ${
                mode === "mixed"
                  ? "bg-purple-500/15 border-purple-500/40 text-purple-200"
                  : "bg-zinc-900 border-white/10 text-zinc-300 hover:border-purple-500/30"
              }`}
            >
              Mixed
            </button>
            <button
              onClick={() => setMode("stratified")}
              className={`flex-1 px-3 py-2 rounded-lg text-sm border ${
                mode === "stratified"
                  ? "bg-purple-500/15 border-purple-500/40 text-purple-200"
                  : "bg-zinc-900 border-white/10 text-zinc-300 hover:border-purple-500/30"
              }`}
            >
              Stratified (no mixing)
            </button>
          </div>

          <label className="block text-sm text-zinc-300 mt-3">
            Bottom temp (°C) — what the coil “sees”
            <input
              className="mt-1 w-full rounded-md bg-zinc-900 border border-white/10 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
              type="number"
              step="1"
              value={bottomC}
              onChange={(e) => setBottomC(Number(e.target.value))}
            />
          </label>

          <label className="block text-sm text-zinc-300 mt-3">
            Top temp (°C) — context only
            <input
              className="mt-1 w-full rounded-md bg-zinc-900 border border-white/10 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
              type="number"
              step="1"
              value={topC}
              onChange={(e) => setTopC(Number(e.target.value))}
              disabled={mode !== "stratified"}
            />
          </label>

          <div className="mt-3 text-xs text-zinc-400">
            In stratified mode, kW is driven by <span className="text-zinc-200">bottom temp</span>, not the average.
          </div>
        </div>
      </div>

      {/* U presets */}
      <div className="mt-4 rounded-2xl border border-white/10 bg-zinc-800/40 p-4">
        <div className="text-xs uppercase tracking-wide text-zinc-400 mb-3">U assumption (outside area basis)</div>

        <div className="flex flex-wrap gap-2">
          {["conservative", "typical", "optimistic"].map((p) => (
            <button
              key={p}
              onClick={() => applyPreset(p)}
              className={`px-3 py-1.5 rounded-full text-sm border ${
                preset === p
                  ? "bg-purple-500/15 border-purple-500/40 text-purple-200"
                  : "bg-zinc-900 border-white/10 text-zinc-300 hover:border-purple-500/30"
              }`}
            >
              {p} ({presets[p]})
            </button>
          ))}
          <div className="flex-1" />
          <label className="text-sm text-zinc-300">
            U
            <input
              className="ml-2 w-28 rounded-md bg-zinc-900 border border-white/10 px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-purple-500"
              type="number"
              step="10"
              value={U}
              onChange={(e) => {
                setPreset("custom");
                setU(Number(e.target.value));
              }}
            />
          </label>
        </div>
      </div>

      {/* Headline */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-3 rounded-2xl bg-purple-500/10 border border-purple-500/30 p-5">
          <div className="text-sm text-purple-200">
            Live point ({mode === "stratified" ? "local bottom temp" : "bulk tank temp"} = {fmt(live.Tcold, 1)}°C)
          </div>
          <div className="mt-1 text-3xl font-bold text-purple-300">{fmt(live.Q_kW, 2)} kW</div>
          <div className="mt-2 text-sm text-purple-200/90">
            Tout ≈ <span className="font-semibold">{fmt(live.Tout, 1)}°C</span> (ΔT ≈ {fmt(live.dT, 1)}°C), LMTD ≈{" "}
            {fmt(live.dTlm, 2)}K
          </div>
          {mode === "stratified" ? (
            <div className="mt-2 text-xs text-purple-100/80">
              Top is {fmt(topC, 1)}°C but coil output is governed by the bottom layer. That’s the whole point of stratification.
            </div>
          ) : null}
        </div>

        <Stat label="Outside area (A)" value={`${fmt(base.Aout, 4)} m²`} sub="A = π·Dout·L" />
        <Stat label="Mass flow (ṁ)" value={`${fmt(base.mdot, 4)} kg/s`} sub={`ρ≈${fmt(base.rho, 1)} kg/m³`} />
        <Stat label="UA" value={`${fmt(base.UA, 1)} W/K`} sub="UA = U · A" />
      </div>

      {/* Table */}
      <div className="mt-6 rounded-2xl border border-white/10 bg-zinc-800/40 overflow-hidden">
        <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold text-zinc-100">
              Outputs at key {mode === "stratified" ? "bottom-layer" : "bulk tank"} temps
            </div>
            <div className="text-xs text-zinc-400">
              These are the same maths — the only difference is what you assume the coil “sees” on the tank side.
            </div>
          </div>
          <Badge tone="zinc">{mode === "stratified" ? "Model: stratified (Tcold=bottom)" : "Model: mixed (Tcold=bulk)"}</Badge>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-zinc-900/60 text-zinc-300">
              <tr>
                <th className="text-left px-5 py-3 font-medium">Tcold (°C)</th>
                <th className="text-left px-5 py-3 font-medium">kW (Q)</th>
                <th className="text-left px-5 py-3 font-medium">Primary Tout (°C)</th>
                <th className="text-left px-5 py-3 font-medium">Primary ΔT (°C)</th>
                <th className="text-left px-5 py-3 font-medium">LMTD (K)</th>
                <th className="text-left px-5 py-3 font-medium">Qmax (kW)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {points.map((p) => (
                <tr key={p.Tcold} className="hover:bg-white/5">
                  <td className="px-5 py-3 text-zinc-200">{p.Tcold}</td>
                  <td className="px-5 py-3 font-semibold text-purple-300">{fmt(p.Q_kW, 2)}</td>
                  <td className="px-5 py-3 text-zinc-200">{fmt(p.Tout, 1)}</td>
                  <td className="px-5 py-3 text-zinc-200">{fmt(p.dT, 1)}</td>
                  <td className="px-5 py-3 text-zinc-200">{fmt(p.dTlm, 2)}</td>
                  <td className="px-5 py-3 text-zinc-400">{fmt(p.Qmax_kW, 1)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="px-5 py-4 border-t border-white/10 text-xs text-zinc-400">
          <span className="text-zinc-200 font-medium">Qmax</span> is the absolute upper bound if the boiler flow cooled all the way to Tcold.
        </div>
      </div>

      {/* Working */}
      <div className="mt-6 rounded-2xl border border-white/10 bg-zinc-800/40 p-5">
        <div className="text-sm font-semibold text-zinc-100 mb-2">Working (what changes when “not mixed”)</div>
        <div className="space-y-1">
          <Row k="Coil area" v={`A = π·Dout·L = π·${fmt(odMm / 1000, 4)}·${fmt(lengthM, 2)} = ${fmt(base.Aout, 4)} m²`} />
          <Row k="Flow to mass flow" v={`ṁ = ρ(Tin)·Q = ${fmt(base.mdot, 4)} kg/s`} />
          <Row k="UA" v={`UA = U·A = ${fmt(U, 0)}·${fmt(base.Aout, 4)} = ${fmt(base.UA, 1)} W/K`} />
          <Row
            k="Key difference"
            v={
              mode === "stratified"
                ? "Tcold = bottom-layer temp around the coil (not average tank temp)."
                : "Tcold = bulk tank temp (well-mixed assumption)."
            }
          />
          <Row k="Solve" v="Q = UA·LMTD with Tout = Tin − Q/(ṁ·cp) (iterated)." />
        </div>

        <div className="mt-3 text-xs text-zinc-400">
          If your cylinder really stays stratified, the coil can keep dumping higher kW for longer because it keeps “seeing” colder water
          at the bottom while the top gets hot. A well-mixed model will under-predict early performance and over-predict end-of-heat-up behaviour.
        </div>
      </div>
    </div>
  );
}
